/**
 * Product truth for the Workflow proof.
 *
 * The extension labels and dimensions below are copied from the English
 * dictionary and the injected 16:9 modal in Extension 0.1.1. The surrounding
 * browser and AI app are deliberately brand-neutral illustrations.
 */
export const EXTENSION_SOURCE_CONTRACT = {
  sourceRelease: "0.1.1",
  manifestVersion: "1.0.1",
  surface: "injected-modal",
  aspectRatio: "16 / 9",
  referenceWidthPx: 960,
  referenceHeightPx: 540,
  leftColumnPx: 238,
  readinessLabel: "Bridge status",
  readinessValue: "Ready to work",
  mode: {
    label: "Mode",
    controls: ["Auto", "Manual"],
    descriptions: {
      auto: "Only new commands from new chat messages are sent to Blender automatically. Existing blocks are ignored.",
      manual: "Each code block gets a Run button that sends only that block to Blender.",
    },
  },
  screenshot: {
    label: "Screenshot mode",
    controls: ["On", "Auto", "Off"],
    descriptions: {
      on: "Send a Blender scene screenshot after every command.",
      auto: "Scene screenshot Blender is sent only if a command explicitly requests it.",
      off: "Never send Blender scene screenshots.",
    },
  },
  actions: ["LLM Instruction", "Share scene", "Share to socials", "Test scenes"],
  siteWork: {
    label: "Work with current site",
    controls: ["Auto Setup", "Site Management"],
  },
  historyLabel: "Bridge command history",
  emptyHistory: "No bridge commands yet.",
  runSingleLabel: "Запустить",
} as const;

export const BROWSER_FRAME_CONTRACT = {
  tabTitle: "Твой ИИ",
  productTitle: "Твой ИИ",
  codeLanguage: "PYTHON",
} as const;

export const AI_APP_FRAME_CONTRACT = {
  windowTitle: "AI приложение",
  conversationTitle: "Сцена в Blender",
  newChatLabel: "Новый чат",
  toolUsed: "Использован Blender Bridge",
} as const;

export const ARTBOARD = { width: 1280, height: 840 } as const;

export interface DemoStep {
  readonly prompt: string;
  readonly lead: string;
  readonly reply: string;
  readonly operationName: string;
  readonly requestId: string;
  readonly code: string;
}

export const DEMO_CHAIN: readonly DemoStep[] = [
  {
    prompt: "Собери сцену из промпта",
    lead: "Собираю сцену по промпту",
    reply: "Сцена собрана в Blender",
    operationName: "Сцена из промпта",
    requestId: "bb_auto_01",
    code: [
      "import bpy",
      "bpy.ops.mesh.primitive_plane_add(size=12)",
      "base = bpy.context.object",
      'base.name = "Scene Base"',
    ].join("\n"),
  },
  {
    prompt: "Доработай сцену по новому промпту",
    lead: "Дорабатываю сцену по новому промпту",
    reply: "Изменения применены к сцене",
    operationName: "Доработка сцены",
    requestId: "bb_auto_02",
    code: [
      "bpy.ops.object.light_add(type='AREA')",
      "key_light = bpy.context.object",
      "key_light.data.energy = 1200",
      "key_light.rotation_euler = (0.4, 0.0, 0.7)",
    ].join("\n"),
  },
  {
    prompt: "Поставь сцену на рендер",
    lead: "Ставлю сцену на рендер",
    reply: "Рендер запущен в Blender",
    operationName: "Рендер сцены",
    requestId: "bb_auto_03",
    code: [
      "scene = bpy.context.scene",
      "scene.render.engine = 'BLENDER_EEVEE_NEXT'",
      "scene.render.filepath = '//render/final.png'",
      "bpy.ops.render.render('INVOKE_DEFAULT', write_still=True)",
    ].join("\n"),
  },
] as const;

export const MCP_SOURCE_CONTRACT = {
  serverName: "blender-bridge-mcp",
  toolName: "execute_blender_code",
  toolTitle: "Execute Blender Code",
  receipt: "Blender code executed successfully.",
} as const;

export const BRIDGE_RESPONSE_CONTRACT = {
  heading: "Bridge response:",
  operationLabel: "Operation:",
  requestLabel: "Request ID:",
  statusLabel: "Status:",
  status: "ok",
} as const;

/** Exact mechanics copied from Extension 0.1.1. */
export const EXTENSION_MOTION_CONTRACT = {
  fabPx: 50,
  fabIconPx: 34,
  stateMs: 140,
  dockMs: 180,
  modalRadiusPx: 22,
  closePx: 32,
} as const;
